import {DateFormat, EventData} from '../types/Event';
import {ImageData} from '../types/Image';
import {ShoeboxLocation} from '../types/Location';
import axios from 'axios';
import {v4 as uuidv4} from 'uuid';

const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

export const predictEventForImages = async (images: ImageData[]): Promise<EventData> => {
    const imageKeys: string[] = images.map((i) => i.key);
    console.log(`[API] predictEventForImages called with ${imageKeys.length} images:`, imageKeys);
    
    try {
        // Get token and log authentication status
        const token = sessionStorage.getItem('token');
        console.log(`[API] Authentication token exists: ${!!token}`);
        
        console.log(`[API] Making POST request to ${API_BASE_URL}/event/predict-details`);
        const response = await axios.post(`${API_BASE_URL}/event/predict-details`,
            { image_keys: imageKeys },
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        console.log(`[API] Received response from predict-details API:`, response.data);

        // Check if event_date exists and is valid
        let eventDate: Date | undefined;
        let dateFormat: DateFormat | undefined;
        
        if (response.data.event_date) {
            // Attempt to parse the date
            eventDate = new Date(response.data.event_date);
            
            // Check if the date is valid
            if (!isNaN(eventDate.getTime())) {
                // Apply timezone offset only for valid dates
                const localOffsetMinutes = eventDate.getTimezoneOffset();
                eventDate.setMinutes(eventDate.getMinutes() + localOffsetMinutes);
                
                // Determine date format only for valid dates
                dateFormat = eventDate.getDate() === 1 ? DateFormat.MONTH : DateFormat.DAY;
                
                console.log(`[API] Valid event date parsed: ${eventDate.toISOString()}`);
            } else {
                console.warn(`[API] Invalid date received from API: ${response.data.event_date}`);
                eventDate = undefined;
            }
        } else {
            console.log(`[API] No event date provided by API`);
        }

        const eventSuggestion: EventData = {
            key: uuidv4(),
            images: images,
            title: response.data.event_title,
            location_description: response.data.event_location, // Use the predicted location as the description
            location: response.data.event_location_structured ? {
                address: response.data.event_location_structured.address,
                city: response.data.event_location_structured.city,
                state: response.data.event_location_structured.state,
                country: response.data.event_location_structured.country,
                geolocation: response.data.event_location_structured.geolocation
            } : undefined,
            date: eventDate, // This will be undefined if the date was invalid
            dateFormat: dateFormat, // This will be undefined if the date was invalid
            keywords: response.data.event_keywords || [],
        };

        console.log('Parsed event prediction: ', eventSuggestion);

        return eventSuggestion;
    } catch (error) {
        console.error('Failed to fetch predictions for event:', error);
        throw new Error('Failed to fetch predictions for event. Please try again later.');
    }
};

interface CreateEventPayload {
    key: string;
    image_keys: string[];
    title?: string;
    location_description?: string;
    location?: {
        address?: string;
        street_address?: string;
        city?: string;
        state?: string;
        country?: string;
        sublocation?: string;
        geolocation?: {
            latitude: number;
            longitude: number;
        };
        static_map_url?: string;
    };
    latitude?: number;
    longitude?: number;
    date?: string;
    date_format?: 'day' | 'month' | 'year';
    keywords?: string[];
    attendee_keys?: string[];
}

export const createEvent = async (event: EventData): Promise<boolean> => {
    try {
        console.log('Creating event: ', event);
        
        // Extract image keys
        const imageKeys = event.images.map(image => image.key);
        
        // Prepare the payload
        const payload: CreateEventPayload = {
            key: event.key,
            image_keys: imageKeys,
            title: event.title,
            location_description: event.location_description,
            keywords: event.keywords,
            date_format: event.dateFormat,
        };
        
        // Handle location data if present
        if (event.location) {
            payload.location = {
                address: event.location.address,
                street_address: event.location.street_address,
                city: event.location.city,
                state: event.location.state,
                country: event.location.country,
                sublocation: event.location.sublocation,
            };
            
            if (event.location.geolocation) {
                payload.location.geolocation = event.location.geolocation;
                payload.latitude = event.location.geolocation.latitude;
                payload.longitude = event.location.geolocation.longitude;
            }
            
            if (event.location.static_map_url) {
                payload.location.static_map_url = event.location.static_map_url;
            }
        }
        
        // Handle date - preserve just the date part (year, month, day)
        if (event.date) {
            const dateObj = new Date(event.date);
            
            // Extract UTC date components to avoid timezone issues
            const year = dateObj.getUTCFullYear();
            const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getUTCDate()).padStart(2, '0');
            
            // Create a date string in YYYY-MM-DD format with noon UTC
            const dateStr = `${year}-${month}-${day}`;
            payload.date = `${dateStr}T12:00:00.000Z`;
        }
        
        // Handle attendees if present
        if (event.attendees && event.attendees.length > 0) {
            payload.attendee_keys = event.attendees.map(person => person.seed_key);
        }
        
        console.log('Sending payload to API:', payload);
        
        // Get the token and ensure it's properly formatted
        const token = sessionStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication token is missing');
        }
        
        const response = await axios.post(`${API_BASE_URL}/events`, // todo: i want this to just be POST /event but need to update the api definition
            payload,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        
        console.log('API response:', response.data);
        
        return true;
    } catch (error) {
        console.error('Failed to create event:', error);
        throw new Error('Failed to create event. Try again later.');
    }
};

export const updateEvent = async (event: Partial<EventData>): Promise<EventData> => {
    try {
        if (!event.key) {
            throw new Error('Event key is required');
        }

        // Create a modified payload
        const payload: any = {
            key: event.key,
            title: event.title,
            location_description: event.location_description,
            keywords: event.keywords,
            date_format: event.dateFormat,
        };
        
        // Handle location data if present
        if (event.location) {
            payload.location = {
                address: event.location.address,
                street_address: event.location.street_address,
                city: event.location.city,
                state: event.location.state,
                country: event.location.country,
                sublocation: event.location.sublocation,
            };
            
            if (event.location.geolocation) {
                payload.location.geolocation = event.location.geolocation;
                payload.latitude = event.location.geolocation.latitude;
                payload.longitude = event.location.geolocation.longitude;
            }
            
            if (event.location.static_map_url) {
                payload.location.static_map_url = event.location.static_map_url;
            }
        }
        
        // Handle date - preserve just the date part (year, month, day)
        if (event.date) {
            const dateObj = new Date(event.date);
            
            // Extract UTC date components to avoid timezone issues
            const year = dateObj.getUTCFullYear();
            const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getUTCDate()).padStart(2, '0');
            
            // Create a date string in YYYY-MM-DD format with noon UTC
            const dateStr = `${year}-${month}-${day}`;
            payload.date = `${dateStr}T12:00:00.000Z`;
        }
        
        // Handle attendees if present
        if (event.attendees && event.attendees.length > 0) {
            payload.attendee_keys = event.attendees.map(person => person.seed_key);
        }

        console.log('Updating event with payload: ', JSON.stringify(payload));

        // Get the token and ensure it's properly formatted
        const token = sessionStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication token is missing');
        }
        
        const response = await axios.patch(`${API_BASE_URL}/events`,
            payload,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        const createdEvent: EventData = response.data;
        console.log('Updated event: ', createdEvent);

        return createdEvent;
    } catch (error) {
        console.error('Failed to update event:', error);
        throw new Error('Failed to update event. Try again later.');
    }
};

// Helper function to adjust ISO date string to user's timezone
export const adjustToLocalTimezone = (isoDateString: string): string => {
    console.log('original: ', isoDateString);
    const date = new Date(isoDateString);

    if (isNaN(date.getTime())) {
        console.warn('Invalid date:', isoDateString);
        return isoDateString;
    }

    const offsetMinutes = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + offsetMinutes);

    console.log('new: ', date.toISOString());
    return date.toISOString();
};


export const fetchEvents = async (): Promise<{ events: EventData[]}> => {
    try {
        // Get the token and ensure it's properly formatted
        const token = sessionStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication token is missing');
        }
        
        const response = await axios.get(`${API_BASE_URL}/events`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        // Transform null values to undefined
        const eventData: EventData[] = response.data.events.map((event: any) => cleanEvent(event));
        return {events: eventData};

    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid parameters provided');
        }
        console.error('Failed to fetch events:', error);
        throw new Error('Failed to load events. Please try again later.');
    }
};

/**
 * Fetch event data for a specific image by its key
 * @param imageKey The unique key of the image
 * @returns The event data associated with the image, or null if no event is found
 */
export const fetchEventByImageKey = async (imageKey: string): Promise<EventData | null> => {
    try {
        // Get the token and ensure it's properly formatted
        const token = sessionStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication token is missing');
        }
        
        const response = await axios.get(`${API_BASE_URL}/events/image/${imageKey}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (response.data && response.data.event) {
            // Transform null values to undefined
            return cleanEvent(response.data.event);
        }
        
        return null; // No event found for this image

    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.status === 404) {
                // Image not found in any event
                return null;
            } else if (error.response?.status === 400) {
                throw new Error(error.response.data.error || 'Invalid parameters provided');
            }
        }
        console.error('Failed to fetch event by image key:', error);
        throw new Error('Failed to load event data. Please try again later.');
    }
};

// Helper function to convert nulls to undefined and normalize dates
const cleanEvent = (event: any): EventData => {
    const cleanField = (value: any) => value === null ? undefined : value;

    // Create a timezone-neutral date object for event date if it exists
    let normalizedDate;
    if (event.date) {
        // Extract the date part directly from the API string
        // This avoids timezone conversion issues
        const dateParts = event.date.split('T')[0].split('-');
        
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
            const day = parseInt(dateParts[2]);
            
            // Create a date at noon UTC using the extracted components
            normalizedDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
        } else {
            // Fallback to previous method
            const dateObj = new Date(event.date);
            normalizedDate = new Date(Date.UTC(
                dateObj.getUTCFullYear(),
                dateObj.getUTCMonth(),
                dateObj.getUTCDate(),
                12, 0, 0
            ));
        }
    }

    return {
        key: event.key,
        title: cleanField(event.title),
        location_description: cleanField(event.location_description),
        location: cleanField(event.location),
        date: normalizedDate,
        dateFormat: cleanField(event.dateFormat),
        keywords: cleanField(event.keywords) || [],
        images: event.images.map((image: any) => {
            // Create a timezone-neutral date for image datetime if it exists
            let normalizedImageDate;
            if (image.datetime) {
                // Extract the date part directly from the API string
                const dateParts = image.datetime.split('T')[0].split('-');
                
                if (dateParts.length === 3) {
                    const year = parseInt(dateParts[0]);
                    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
                    const day = parseInt(dateParts[2]);
                    
                    // Create a date at noon UTC using the extracted components
                    normalizedImageDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
                } else {
                    // Fallback to previous method
                    const imageDateObj = new Date(image.datetime);
                    normalizedImageDate = new Date(Date.UTC(
                        imageDateObj.getUTCFullYear(),
                        imageDateObj.getUTCMonth(),
                        imageDateObj.getUTCDate(),
                        12, 0, 0
                    ));
                }
            }
            
            return {
                key: image.key,
                thumbnail_url: image.thumbnail_url,
                image_url: image.image_url,
                datetime: normalizedImageDate,
                event_key: cleanField(image.event_key),
            };
        })
    };
};